import {
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	type ComponentProps,
} from 'react';

import { css, keyframes } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button-v25/new';
import Flag from '@atlaskit/flag';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import CrossIcon from '@atlaskit/icon/core/cross';
import { token } from '@atlaskit/tokens';

import { useNotifyDemoComplete } from '../components/jsm-playground-demo-timeline';
import { usePrefersReducedMotion } from '../components/jsm-playground-motion';
import { useJsmPlaygroundAnalytics } from '../use-jsm-playground-analytics';

import { JsmPlaygroundAdamCursor, ADAM_CURSOR_TRAVEL_MS } from './jsm-playground-adam-cursor';
import {
	ENGINEERING_QUEUE_ROWS,
	ENGINEERING_WORKFLOW,
	type EngineeringWorkflowStatus,
} from './jsm-playground-engineering-data';
import { JsmPlaygroundEngineeringDetail } from './jsm-playground-engineering-detail';
import { engineeringMessages as messages } from './jsm-playground-engineering-messages';
import { JsmPlaygroundEngineeringQueue } from './jsm-playground-engineering-queue';
import { EngineeringStatusDropdown } from './jsm-playground-engineering-status';
import { JsmPlaygroundEngineeringToolbar } from './jsm-playground-engineering-toolbar';

export type EngineeringDemoState = 'autoplay' | 'initial' | 'complete';

type JsmPlaygroundEngineeringProps = {
	demoState?: EngineeringDemoState;
	hasUserTakenOver: boolean;
	onDemoComplete?: () => void;
};

const PANEL_TRANSITION_MS = 260;
const BACKLOG_STATUS = ENGINEERING_WORKFLOW[4];
type IconButtonIcon = NonNullable<ComponentProps<typeof IconButton>['icon']>;

const DismissIcon: IconButtonIcon = () => <CrossIcon label="" size="small" />;
const getSelectedRow = (index: number | null) =>
	index === null ? null : (ENGINEERING_QUEUE_ROWS[index] ?? null);

const cursorIn = 600;
const rowPress = cursorIn + ADAM_CURSOR_TRAVEL_MS + 500;
const rowClicked = rowPress + 150;
const cursorOutAfterRow = rowClicked + 400;
const retargetStatus = cursorOutAfterRow + PANEL_TRANSITION_MS + 250;
const statusCursorIn = retargetStatus + 150;
const statusPress = statusCursorIn + ADAM_CURSOR_TRAVEL_MS + 500;
const menuOpen = statusPress + 200;
const cursorToBacklog = menuOpen + 250;
const backlogPress = cursorToBacklog + ADAM_CURSOR_TRAVEL_MS;
const statusChanged = backlogPress + 200;
const cursorOut = statusChanged + 400;
const flagInAt = cursorOut + 200;
const FLAG_ENTER_MS = 320;

export const ENGINEERING_TIMELINE = {
	cursorIn,
	rowPress,
	rowClicked,
	cursorOutAfterRow,
	retargetStatus,
	statusCursorIn,
	statusPress,
	menuOpen,
	cursorToBacklog,
	backlogPress,
	statusChanged,
	cursorOut,
	flagIn: flagInAt,
} as const;

type CursorTarget = 'row' | 'status' | 'backlog';
type CursorState = {
	isPressed: boolean;
	isTransitionEnabled: boolean;
	isVisible: boolean;
	target: CursorTarget;
};
type Point = { x: number; y: number };

const JsmPlaygroundEngineeringComponent = ({
	demoState = 'autoplay',
	hasUserTakenOver,
	onDemoComplete,
}: JsmPlaygroundEngineeringProps) => {
	const intl = useIntl();
	const prefersReducedMotion = usePrefersReducedMotion();
	const notifyDemoComplete = useNotifyDemoComplete(onDemoComplete);
	const shouldStartComplete = demoState === 'complete' || prefersReducedMotion;
	const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(
		shouldStartComplete ? 0 : null,
	);
	const [statusOverrides, setStatusOverrides] = useState<Record<number, EngineeringWorkflowStatus>>(
		shouldStartComplete ? { 0: BACKLOG_STATUS } : {},
	);
	const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
	const [isFlagVisible, setIsFlagVisible] = useState(shouldStartComplete);
	const [isFlagDismissed, setIsFlagDismissed] = useState(false);
	const [cursor, setCursor] = useState<CursorState>({
		isPressed: false,
		isTransitionEnabled: false,
		isVisible: false,
		target: 'row',
	});
	const [cursorPoints, setCursorPoints] = useState<Partial<Record<CursorTarget, Point>>>({});
	const containerRef = useRef<HTMLDivElement>(null);
	const firstRowRef = useRef<HTMLDivElement>(null);
	const tableRef = useRef<HTMLDivElement>(null);
	const statusTriggerRef = useRef<HTMLElement>(null);
	const backlogItemRef = useRef<HTMLDivElement>(null);
	const [tableHeight, setTableHeight] = useState<number>();
	const takeoverRef = useRef(hasUserTakenOver);
	takeoverRef.current = hasUserTakenOver;
	const { firePlaygroundScreenNavigated } = useJsmPlaygroundAnalytics('engineering');
	const setRowStatus = useCallback(
		(index: number, status: EngineeringWorkflowStatus) =>
			setStatusOverrides((current) => ({ ...current, [index]: status })),
		[],
	);

	const measureTarget = useCallback(
		(element: HTMLElement | null, xOffset: number): Point | undefined => {
			const container = containerRef.current;
			if (!container || !element) return undefined;
			const containerRect = container.getBoundingClientRect();
			const targetRect = element.getBoundingClientRect();
			const scale = containerRect.width / container.offsetWidth || 1;
			return {
				x: (targetRect.left - containerRect.left + xOffset) / scale,
				y: (targetRect.top - containerRect.top + targetRect.height / 2) / scale,
			};
		},
		[],
	);

	useLayoutEffect(() => {
		const measureRow = () => {
			const point = measureTarget(firstRowRef.current, 140);
			if (point) setCursorPoints((current) => ({ ...current, row: point }));
		};
		measureRow();

		const container = containerRef.current;
		if (!container || typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver(measureRow);
		observer.observe(container);
		return () => observer.disconnect();
	}, [measureTarget]);

	useLayoutEffect(() => {
		const table = tableRef.current;
		if (!table) return;

		const measure = () => {
			const height = table.offsetHeight;
			if (height) setTableHeight(height);
		};
		measure();

		if (typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver(measure);
		observer.observe(table);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (demoState !== 'autoplay' || prefersReducedMotion) return;
		const timers: number[] = [];
		const schedule = (delay: number, action: () => void) => {
			timers.push(
				window.setTimeout(() => {
					if (!takeoverRef.current) action();
				}, delay),
			);
		};

		schedule(ENGINEERING_TIMELINE.cursorIn, () =>
			setCursor({ isPressed: false, isTransitionEnabled: true, isVisible: true, target: 'row' }),
		);
		schedule(ENGINEERING_TIMELINE.rowPress, () =>
			setCursor((current) => ({ ...current, isPressed: true })),
		);
		schedule(ENGINEERING_TIMELINE.rowClicked, () => {
			setSelectedRowIndex(0);
			setCursor((current) => ({ ...current, isPressed: false }));
		});
		schedule(ENGINEERING_TIMELINE.cursorOutAfterRow, () =>
			setCursor((current) => ({ ...current, isVisible: false })),
		);
		schedule(ENGINEERING_TIMELINE.retargetStatus, () => {
			const point = measureTarget(statusTriggerRef.current, 34);
			if (point) setCursorPoints((current) => ({ ...current, status: point }));
			setCursor({
				isPressed: false,
				isTransitionEnabled: true,
				isVisible: false,
				target: 'status',
			});
		});
		schedule(ENGINEERING_TIMELINE.statusCursorIn, () =>
			setCursor((current) => ({ ...current, isVisible: true })),
		);
		schedule(ENGINEERING_TIMELINE.statusPress, () =>
			setCursor((current) => ({ ...current, isPressed: true })),
		);
		schedule(ENGINEERING_TIMELINE.menuOpen, () => {
			setIsStatusMenuOpen(true);
			setCursor((current) => ({ ...current, isPressed: false }));
		});
		schedule(ENGINEERING_TIMELINE.cursorToBacklog, () => {
			const point = measureTarget(backlogItemRef.current, 24);
			if (point) setCursorPoints((current) => ({ ...current, backlog: point }));
			setCursor((current) => ({ ...current, target: 'backlog' }));
		});
		schedule(ENGINEERING_TIMELINE.backlogPress, () =>
			setCursor((current) => ({ ...current, isPressed: true })),
		);
		schedule(ENGINEERING_TIMELINE.statusChanged, () => {
			setRowStatus(0, BACKLOG_STATUS);
			setIsStatusMenuOpen(false);
			setCursor((current) => ({ ...current, isPressed: false }));
		});
		schedule(ENGINEERING_TIMELINE.cursorOut, () =>
			setCursor((current) => ({ ...current, isVisible: false })),
		);
		schedule(ENGINEERING_TIMELINE.flagIn, () => setIsFlagVisible(true));

		return () => timers.forEach(window.clearTimeout);
	}, [demoState, measureTarget, prefersReducedMotion, setRowStatus]);

	useEffect(() => {
		if (!prefersReducedMotion || demoState !== 'autoplay') return;
		setSelectedRowIndex(0);
		setRowStatus(0, BACKLOG_STATUS);
		setIsStatusMenuOpen(false);
		setIsFlagVisible(true);
		setCursor((current) => ({ ...current, isVisible: false }));
	}, [demoState, prefersReducedMotion, setRowStatus]);

	useEffect(() => {
		if (hasUserTakenOver) setCursor((current) => ({ ...current, isVisible: false }));
	}, [hasUserTakenOver]);

	useEffect(() => {
		if (shouldStartComplete || demoState !== 'autoplay') {
			if (shouldStartComplete) {
				notifyDemoComplete();
			}
			return;
		}

		const timer = window.setTimeout(() => {
			if (!takeoverRef.current) {
				notifyDemoComplete();
			}
		}, ENGINEERING_TIMELINE.flagIn + FLAG_ENTER_MS);
		return () => window.clearTimeout(timer);
	}, [demoState, notifyDemoComplete, shouldStartComplete]);

	const selectStatus = useCallback(
		(status: EngineeringWorkflowStatus) => {
			if (selectedRowIndex !== null) {
				setRowStatus(selectedRowIndex, status);
			}
			setIsStatusMenuOpen(false);
			if (status.label === BACKLOG_STATUS.label) setIsFlagVisible(true);
		},
		[selectedRowIndex, setRowStatus],
	);

	const selectedRow = getSelectedRow(selectedRowIndex);
	const activeCursorPoint = cursorPoints[cursor.target];

	return (
		<div css={pageStyles} data-testid="jsm-playground-engineering" ref={containerRef}>
			<JsmPlaygroundEngineeringToolbar />

			<div css={contentStyles}>
				<JsmPlaygroundEngineeringQueue
					firstRowRef={firstRowRef}
					onOpenRow={(index) => {
						const nextIndex = selectedRowIndex === index ? null : index;
						if (nextIndex !== null) {
							firePlaygroundScreenNavigated({
								team_type: 'engineering',
								screen_id: `queue_detail_IT-${350 + nextIndex}`,
								interaction_mode: 'manual_click',
							});
						} else {
							firePlaygroundScreenNavigated({
								team_type: 'engineering',
								screen_id: 'incoming_requests_queue',
								interaction_mode: 'manual_click',
							});
						}
						setSelectedRowIndex(nextIndex);
					}}
					onStatusChange={setRowStatus}
					selectedRowIndex={selectedRowIndex}
					statusOverrides={statusOverrides}
					tableRef={tableRef}
				/>
				<div
					css={[detailWrapperStyles, selectedRowIndex !== null && detailWrapperOpenStyles]}
					aria-hidden={selectedRowIndex === null}
				>
					{selectedRowIndex !== null && selectedRow && (
						<JsmPlaygroundEngineeringDetail
							height={tableHeight}
							issueKey={`IT-${350 + selectedRowIndex}`}
							key={selectedRowIndex}
							onClose={() => {
								firePlaygroundScreenNavigated({
									team_type: 'engineering',
									screen_id: 'incoming_requests_queue',
									interaction_mode: 'manual_click',
								});
								setSelectedRowIndex(null);
							}}
							row={{
								...selectedRow,
								status: statusOverrides[selectedRowIndex] ?? selectedRow.status,
							}}
							showSlaLabel={selectedRowIndex < 3}
							statusControl={
								<EngineeringStatusDropdown
									isOpen={isStatusMenuOpen}
									onOpenChange={setIsStatusMenuOpen}
									onSelect={selectStatus}
									status={statusOverrides[selectedRowIndex] ?? selectedRow.status}
									backlogItemRef={backlogItemRef}
									triggerRef={statusTriggerRef}
								/>
							}
						/>
					)}
				</div>
			</div>

			{demoState === 'autoplay' && !prefersReducedMotion && activeCursorPoint && (
				<JsmPlaygroundAdamCursor
					hasUserTakenOver={hasUserTakenOver}
					isPressed={cursor.isPressed}
					isTransitionEnabled={cursor.isTransitionEnabled}
					isVisible={cursor.isVisible}
					x={activeCursorPoint.x}
					y={activeCursorPoint.y}
				/>
			)}

			{isFlagVisible && !isFlagDismissed && (
				<div css={flagStyles} data-testid="engineering-backlog-success">
					<Flag
						appearance="normal"
						actions={[{ content: intl.formatMessage(messages.viewWorkItem), onClick: () => {} }]}
						description={
							<FormattedMessage
								{...messages.moveSucceededDescription}
								values={{ strong: (chunks) => <strong css={boldTextStyles}>{chunks}</strong> }}
							/>
						}
						icon={<CheckCircleIcon color={token('color.icon.success')} label="" />}
						id="engineering-backlog-success"
						title={intl.formatMessage(messages.moveSucceeded)}
					/>
					<div css={flagDismissStyles}>
						<IconButton
							appearance="subtle"
							icon={DismissIcon}
							label={intl.formatMessage(messages.dismiss)}
							onClick={() => setIsFlagDismissed(true)}
							spacing="compact"
						/>
					</div>
				</div>
			)}
		</div>
	);
};

// Memoised so the playground root's cursor state changes (enter, hover, press, leave) do not
// re-render this subtree. Its props are primitives plus a stable callback.
export const JsmPlaygroundEngineering = memo(JsmPlaygroundEngineeringComponent);

const pageStyles = css({
	position: 'relative',
	display: 'flex',
	height: '100%',
	minHeight: 0,
	flexDirection: 'column',
	overflow: 'hidden',
	backgroundColor: token('elevation.surface'),
});
const boldTextStyles = css({ fontWeight: token('font.weight.bold') });
const contentStyles = css({
	display: 'flex',
	flex: '1 1 auto',
	minHeight: 0,
	alignItems: 'flex-start',
	paddingBlockEnd: token('space.300'),
	paddingInline: token('space.300'),
});
const detailWrapperStyles = css({
	boxSizing: 'border-box',
	width: 0,
	flexShrink: 0,
	overflow: 'hidden',
	opacity: 0,
	paddingInlineStart: 0,
	transition: `width ${PANEL_TRANSITION_MS}ms cubic-bezier(0.2, 0, 0, 1), padding ${PANEL_TRANSITION_MS}ms cubic-bezier(0.2, 0, 0, 1), opacity 180ms ease-out`,
	'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
});
const detailWrapperOpenStyles = css({
	width: '372px',
	paddingInlineStart: token('space.300'),
	opacity: 1,
});
const flagIn = keyframes({
	from: { opacity: 0, transform: 'translateY(16px)' },
	to: { opacity: 1, transform: 'translateY(0)' },
});
const flagStyles = css({
	position: 'absolute',
	insetBlockEnd: token('space.300'),
	insetInlineStart: token('space.300'),
	zIndex: 720,
	width: '400px',
	animationName: flagIn,
	animationDuration: `${FLAG_ENTER_MS}ms`,
	animationTimingFunction: 'cubic-bezier(0.33, 0, 0.2, 1)',
	'@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
});
const flagDismissStyles = css({
	position: 'absolute',
	insetBlockStart: token('space.100'),
	insetInlineEnd: token('space.100'),
});
